import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { Container, Section } from 'chaoskit/src/components'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { useTheme } from '@emotion/react'
import produce from 'immer'
import { isEmpty } from 'lodash-es'
import { text } from 'chaoskit/src/assets/styles/utility'
import { MDXProvider } from '@mdx-js/react'

import { ReactComponent as ResourceReadSvg } from '~media/reliability/resource-read.svg'
import { ReactComponent as ResourceWatchSvg } from '~media/reliability/resource-watch.svg'
import {
  Hero,
  getReliabilityImage,
  ReliabilityGrid,
  ReliabilityGridAside,
  ReliabilityGridContent,
  ReliabilityNextTeaser,
} from '~partials/reliability/UI'
import BaseLayout from '~layouts/BaseLayout'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import HelmetSEO from '~partials/global/HelmetSEO'
import TableOfContents from '~components/TableOfContents'
import AutoLinkHeader from '~components/AutoLinkHeader'
import TextLink from '~components/TextLink'
import KoltonVideo from '~components/reliability/KoltonVideo'
import ArticleFooter from '~partials/global/ArticleFooter'

const AdditionalResourcesCard = ({ data }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        position: 'relative',
        background: theme.color.light.base,
        padding: theme.space.base,
        display: 'grid',
        alignContent: 'center',

        [theme.mq.medium]: {
          padding: theme.space.medium,
        },
      }}
    >
      <TextLink
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',

          '&:hover, &:focus': {
            '+ * .AdditionalResource__Icon': {
              transform: 'scale(1.05)',
            },
          },
        }}
        href={data.link}
      />
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridAutoColumn: 'auto',
          gridAutoFlow: 'column',
          justifyContent: 'start',
          gap: theme.space.base,
        }}
      >
        {data.label === 'Read' ? (
          <ResourceReadSvg
            className="AdditionalResource__Icon"
            css={{
              transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
              width: theme.height.base,
            }}
          />
        ) : (
          <ResourceWatchSvg
            className="AdditionalResource__Icon"
            css={{
              transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
              width: theme.height.base,
            }}
          />
        )}
        <div>
          <div
            css={{
              ...text.expanded(theme),
              ...theme.text.xsmall,
              color: theme.fontColor.muted,
              marginBottom: theme.space.xsmall,
            }}
          >
            {data.label}
          </div>
          <div css={{ ...theme.text.small, fontWeight: theme.fontWeight.bold }}>
            {data.title}
          </div>
        </div>
      </div>
    </div>
  )
}

AdditionalResourcesCard.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.oneOf(['Read', 'Watch']),
    link: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
}

const ReliabilitySingle = ({
  data: { article },
  pageContext: { previous, next, nodeType },
}) => {
  const theme = useTheme()

  const tocItems = produce(article.tableOfContents, (toc) => {
    toc.items.push({
      url: '#additional-resources',
      title: 'Additional resources',
    })
  })

  return (
    <BaseLayout>
      <HelmetSEO
        title={article.frontmatter.title}
        description={article.frontmatter.description}
        image={article.frontmatter.socialMediaImage}
        noIndex={article.frontmatter.hideFromResources}
      />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero
        title={article.frontmatter.title}
        blurb={article.frontmatter.subTitle}
        image={getReliabilityImage(article.fields.slug)}
      />
      <Container>
        <Section>
          <MDXRenderer>{article.frontmatter.introContent}</MDXRenderer>
        </Section>
        <Section css={{ paddingTop: '0 !important' }}>
          <ReliabilityGrid>
            <ReliabilityGridAside>
              <TableOfContents tree={tocItems} />
            </ReliabilityGridAside>
            <ReliabilityGridContent>
              <MDXProvider
                components={{
                  KoltonVideo,
                }}
              >
                <MDXRenderer>{article.body}</MDXRenderer>
              </MDXProvider>

              {article.frontmatter.additionalResources && (
                <Fragment>
                  <AutoLinkHeader id="additional-resources" as="h2">
                    Additional resources
                  </AutoLinkHeader>
                  <div
                    css={{
                      background: theme.color.primary.base,
                      display: 'grid',
                      gap: 1,

                      [theme.mq.small]: {
                        gridTemplateColumns: 'repeat(2, 1fr)',
                      },
                    }}
                  >
                    {article.frontmatter.additionalResources.map((resource) => (
                      <AdditionalResourcesCard
                        key={resource.title}
                        data={{
                          label: resource.label,
                          link: resource.link,
                          title: resource.title,
                        }}
                      />
                    ))}
                    {/* Used to fill-up last item if we have an odd-number to prevent the background border from seeping-through */}
                    {article.frontmatter.additionalResources.length % 2 !==
                      0 && (
                      <div
                        css={{
                          background: theme.color.light.base,
                        }}
                      />
                    )}
                  </div>
                </Fragment>
              )}
              <ArticleFooter
                navigation={{
                  previous: isEmpty(previous) ? null : previous,
                  next: isEmpty(next) ? null : next,
                }}
                homeLabel="Reliability"
                homeUrl="/reliability/"
              />
            </ReliabilityGridContent>
          </ReliabilityGrid>
        </Section>
      </Container>
      {!isEmpty(next) && <ReliabilityNextTeaser article={next} />}
    </BaseLayout>
  )
}

ReliabilitySingle.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default ReliabilitySingle

export const pageQuery = graphql`
  query ReliabilitySingle($slug: String!) {
    article: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/reliability/" }
    ) {
      ...ReliabilityArticleFragment
    }
  }
`
